import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, {
	CardBody,
	CardFooter,
	CardFooterLeft,
	CardFooterRight,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import useDarkMode from '../../../hooks/useDarkMode';
import { dashboardPagesMenu, demoPagesMenu } from '../../../menu';
import SubHeader, {
	SubHeaderLeft,
	SubHeaderRight,
	SubheaderSeparator,
} from '../../../layout/SubHeader/SubHeader';
import Breadcrumb from '../../../components/bootstrap/Breadcrumb';
import Accordion, { AccordionItem } from '../../../components/bootstrap/Accordion';
import Icon from '../../../components/icon/Icon';
import { toast } from 'react-toastify';
import { useChangePassword } from '../../../apis/hooks/auth';
import ReactSelect from 'react-select';
import ImageViewer from '../../../ImageViewer';
import Empty from '../../../assets/empty.jpeg';
import * as Yup from 'yup';
import { contactNumberSchema, handleContactChange } from '../../../utils/contactValidation';
import { useDropdownListCity, useDropdownListState } from '../../../apis/hooks/area';
import { useDropdownListPlaceOfSupplier } from '../../../apis/hooks/placeOfSupply';
import {
	useAddCompanyProfileDoc,
	useDeleteCompanyProfileDoc,
	useGetCompanyProfileById,
	useUpdateCompanyProfileById,
	useUploadCompanyProfileDoc,
} from '../../../apis/hooks/companyProfile';

interface ChangePasswordFormValues {
	validationName: string;
	validationContactPerson: string;
	validationStatus: number;
	validationAddress: string;
	validationZip: string;
	validationState: string;
	validationCity: string;
	validationEmail: string;
	validationMobile: string;
	validationPhone: string;
	validationPan: string;
	validationGst: string;
	validationFAX: string;
	validationWebsite: string;
	validationPlaceOfSupply: string;

	validationImage: string;
}

const accountValidationSchema = Yup.object({
	validationContact: contactNumberSchema,
	// other fields...
});

const UpdateCompanyProfile = () => {
	const { darkModeStatus } = useDarkMode();

	// City Dropdown
	const [dataCity, setDataCity] = useState<any[]>([]);
	const [cityPage, setCityPage] = useState<number>(1);
	const [citySearch, setCitySearch] = useState<string>('');
	const [cityHasMore, setCityHasMore] = useState<boolean>(true);
	const [cityIsLoading, setCityIsLoading] = useState<boolean>(false);
	// State Dropdown
	const [dataState, setDataState] = useState<any[]>([]);
	const [statePage, setStatePage] = useState<number>(1);
	const [stateSearch, setStateSearch] = useState<string>('');
	const [stateHasMore, setStateHasMore] = useState<boolean>(true);
	const [stateIsLoading, setStateIsLoading] = useState<boolean>(false);
	// PlaceOfSupply Dropdown
	const [dataPlaceOfSupply, setDataPlaceOfSupply] = useState<any[]>([]);
	const [placeOfSupplyPage, setPlaceOfSupplyPage] = useState<number>(1);
	const [placeOfSupplySearch, setPlaceOfSupplySearch] = useState<string>('');
	const [placeOfSupplyHasMore, setPlaceOfSupplyHasMore] = useState<boolean>(true);
	const [placeOfSupplyIsLoading, setPlaceOfSupplyIsLoading] = useState<boolean>(false);

	const [image, setImage] = useState<any[]>([]);
	const [filepath, setFilepath] = useState<string>('');

	const { mutate: listCity, isPending: cityPending } = useDropdownListCity();
	const { mutate: listState, isPending: statePending } = useDropdownListState();
	const { mutate: listPlaceOfSupply, isPending: placeOfSupplyPending } =
		useDropdownListPlaceOfSupplier();
	const { mutate: getCompanyProfileById, isPending } = useGetCompanyProfileById();
	const { mutate: updateCompanyProfile } = useUpdateCompanyProfileById();
	const { mutate: uploadCompanyProfileDoc } = useUploadCompanyProfileDoc();
	const { mutate: attachCompanyProfileDoc } = useAddCompanyProfileDoc();
	const { mutate: deleteCompanyProfileDoc } = useDeleteCompanyProfileDoc();

	const formik = useFormik<ChangePasswordFormValues>({
		initialValues: {
			validationName: '',
			validationContactPerson: '',
			validationStatus: 0,
			validationAddress: '',
			validationZip: '',
			validationState: '',
			validationCity: '',
			validationEmail: '',
			validationPhone: '',
			validationMobile: '',
			validationPan: '',
			validationGst: '',
			validationPlaceOfSupply: '',
			validationFAX: '',
			validationWebsite: '',
			validationImage: '',
		},
		validate: (values) => {
			const errors: Partial<Record<keyof ChangePasswordFormValues, string>> = {};
			// Validation logic:
			if (!values.validationName) {
				// Show "Required" error if field is empty and untouched
				errors.validationName = 'Required';
			} else if (!values.validationName.trim()) {
				// Show "Name cannot be just spaces" if field contains only spaces
				errors.validationName = 'Name cannot be just spaces';
			}
			return errors;
		},
		onSubmit: (values) => {
			const payload = {
				name: values.validationName,
				contact_person: values.validationContactPerson,
				address: values.validationAddress,
				pincode: values.validationZip,
				mobile: values.validationMobile,
				phone: values.validationPhone,
				fax: values.validationFAX,
				email: values.validationEmail,
				website: values.validationWebsite,
				city: values.validationCity,
				state: values.validationState,
				pan: values.validationPan,
				gst: values.validationGst,
				// place_of_supply: '68220a7667a7ca8816a2939a', // place of supply _id
				place_of_supply: values.validationPlaceOfSupply, // place of supply _id
			};

			updateCompanyProfile(
				{ _id: '6832b9ba6bade21500d582ea', ...payload },
				{
					onSuccess: (res: any) => {
						if (res?.status) {
							toast.success(res?.message, {
								closeOnClick: true,
								closeButton: true,
							});
						} else {
							toast.error(res?.message);
						}
					},
				},
			);
		},
	});

	const handleUploadDoc = async (e: any) => {
		e.preventDefault();
		setFilepath(URL.createObjectURL(e.target.files[0]));
		const file = e.target.files[0];

		const formData = new FormData();
		formData.append('doc', file); // or just 'images' if backend supports it
		formData.append('module', 'Company'); // add other fields if needed
		const res = await uploadCompanyProfileDoc(formData, {
			onSuccess: async (res: any) => {
				if (res?.status) {
					// formik.setFieldValue('validationImage', res?.data);
					await attachCompanyProfileDoc(
						{ moduleId: '6832b9ba6bade21500d582ea', module: 'Company', doc: res?.data },
						{
							onSuccess: async (doc_res: any) => {
								const inputElement = document.getElementById(
									'accountImage',
								) as HTMLInputElement;

								// If the input element exists, reset its value to null (clear the input)
								if (inputElement) {
									console.log(inputElement);
									inputElement.value = '';
								}

								// Check if doc_res?.data is an array, if not wrap it in an array
								const newImages = Array.isArray(doc_res?.data)
									? doc_res?.data
									: [doc_res?.data];
								setImage((prevImages) => [...(prevImages || []), ...newImages]);
							},
						},
					);

					toast.success(res?.message, {
						closeOnClick: true,
						closeButton: true,
					});
				} else {
					toast.error(res?.message);
				}
			},
			onError: (error: any) => {
				console.error('Create API failed:', error);
			},
		});
	};

	const handleDeleteImage = async (index: number, img_id: string) => {
		const newImages = image.filter((_, i) => i !== index);
		const res = await deleteCompanyProfileDoc(
			{ docId: img_id },
			{
				onSuccess: async (res: any) => {
					if (res?.status) {
						setImage(newImages);
						toast.success(res?.message, {
							closeOnClick: true,
							closeButton: true,
						});
					} else {
						toast.error(res?.message);
					}
				},
				onError: (error: any) => {
					console.error('Create API failed:', error);
				},
			},
		);
	};

	// City Dropdown
	const fetchCity = ({
		pageNo = 1,
		searchString = '',
		isNewSearch = false,
		selectedValue = '',
	}) => {
		if (cityPending || (!cityHasMore && !isNewSearch)) return;

		setCityIsLoading(true);
		listCity(
			{ pageNo, perPage: 10, searchString },
			{
				onSuccess: (response) => {
					const city = response?.data?.map((data) => ({
						value: data?._id,
						label: data?.name,
					}));

					const selectedOption = city.find((opt) => opt.value === selectedValue);

					if (isNewSearch) {
						const merged = selectedOption
							? [
									selectedOption,
									...city.filter((a) => a.value !== selectedOption.value),
								]
							: city;

						setDataCity(merged);
					} else {
						setDataCity((prev) => [...prev, ...city]);
					}

					setCityHasMore(city?.length === 10);
					setCityPage(pageNo + 1);
				},
				onError: (error) => {
					console.error('Dropdown fetch error', error);
				},
				onSettled: () => {
					setCityIsLoading(false);
				},
			},
		);
	};

	// State Dropdown
	const fetchState = ({
		pageNo = 1,
		searchString = '',
		isNewSearch = false,
		selectedValue = '',
	}) => {
		if (statePending || (!stateHasMore && !isNewSearch)) return;

		setStateIsLoading(true);
		listState(
			{ pageNo, perPage: 10, searchString },
			{
				onSuccess: (response) => {
					const state = response?.data?.map((data) => ({
						value: data?._id,
						label: data?.name,
					}));

					const selectedOption = state.find((opt) => opt.value === selectedValue);

					if (isNewSearch) {
						const merged = selectedOption
							? [
									selectedOption,
									...state.filter((a) => a.value !== selectedOption.value),
								]
							: state;

						setDataState(merged);
					} else {
						setDataState((prev) => [...prev, ...state]);
					}

					setStateHasMore(state?.length === 10);
					setStatePage(pageNo + 1);
				},
				onError: (error) => {
					console.error('Dropdown fetch error', error);
				},
				onSettled: () => {
					setStateIsLoading(false);
				},
			},
		);
	};

	// PlaceOfSupply Dropdown
	const fetchPlaceOfSupply = ({
		pageNo = 1,
		searchString = '',
		isNewSearch = false,
		selectedValue = '',
	}) => {
		if (placeOfSupplyPending || (!placeOfSupplyHasMore && !isNewSearch)) return;

		setPlaceOfSupplyIsLoading(true);
		listPlaceOfSupply(
			{ pageNo, perPage: 10, searchString },
			{
				onSuccess: (response) => {
					const placeOfSupply = response?.data?.map((data) => ({
						value: data?._id,
						label: data?.name,
					}));

					const selectedOption = placeOfSupply.find((opt) => opt.value === selectedValue);

					if (isNewSearch) {
						const merged = selectedOption
							? [
									selectedOption,
									...placeOfSupply.filter(
										(a) => a.value !== selectedOption.value,
									),
								]
							: placeOfSupply;

						setDataPlaceOfSupply(merged);
					} else {
						setDataPlaceOfSupply((prev) => [...prev, ...placeOfSupply]);
					}

					setPlaceOfSupplyHasMore(placeOfSupply?.length === 10);
					setPlaceOfSupplyPage(pageNo + 1);
				},
				onError: (error) => {
					console.error('Dropdown fetch error', error);
				},
				onSettled: () => {
					setPlaceOfSupplyIsLoading(false);
				},
			},
		);
	};

	// City Search for Dropdown
	useEffect(() => {
		const handler = setTimeout(() => {
			if (citySearch.trim() !== '') {
				fetchCity({
					pageNo: 1,
					searchString: citySearch.trim(),
					isNewSearch: true,
				});
			}
		}, 500); // delay in ms

		return () => {
			clearTimeout(handler); // cancel if still typing
		};
	}, [citySearch]);

	// State Search for Dropdown
	useEffect(() => {
		const handler = setTimeout(() => {
			if (stateSearch.trim() !== '') {
				fetchState({
					pageNo: 1,
					searchString: stateSearch.trim(),
					isNewSearch: true,
				});
			}
		}, 500); // delay in ms

		return () => {
			clearTimeout(handler); // cancel if still typing
		};
	}, [stateSearch]);

	// PlaceOfSupply Search for Dropdown
	useEffect(() => {
		const handler = setTimeout(() => {
			if (placeOfSupplySearch.trim() !== '') {
				fetchPlaceOfSupply({
					pageNo: 1,
					searchString: placeOfSupplySearch.trim(),
					isNewSearch: true,
				});
			}
		}, 500); // delay in ms

		return () => {
			clearTimeout(handler); // cancel if still typing
		};
	}, [placeOfSupplySearch]);

	// Get Detail for Purchase Order
	useEffect(() => {
		getCompanyProfileById(
			{ _id: '6832b9ba6bade21500d582ea' },
			{
				onSuccess: (res: any) => {
					formik.setValues({
						validationName: res?.name || '',
						validationContactPerson: res?.contact_person || '',
						validationStatus: 0,
						validationAddress: res?.address || '',
						validationZip: res?.pincode || '',
						validationState: res?.stateId || '',
						validationCity: res?.cityId || '',
						validationEmail: res?.email || '',
						validationPhone: res?.phone || '',
						validationMobile: res?.mobile || '',
						validationPan: res?.pan || '',
						validationGst: res?.gst || '',
						validationPlaceOfSupply: res?.placeOfSupplyId || '',
						validationFAX: res?.fax || '',
						validationWebsite: res?.website || '',
						validationImage: res?.logo || '',
					});
				},
			},
		);
	}, []);

	return (
		<PageWrapper title={dashboardPagesMenu.updateCompanyProfilePage.text}>
			<SubHeader>
				<SubHeaderLeft>
					<Breadcrumb
						list={[
							{ title: 'Company Profile', to: '/company-profile/update' },
							{
								title: 'Company Profile',
								to: '#',
							},
						]}
					/>
				</SubHeaderLeft>
				<SubHeaderRight>
					<SubheaderSeparator />
					<Button icon='ArrowBackIosNew' color='primary' isLight tag='a' to='/'>
						Go Back
					</Button>
				</SubHeaderRight>
			</SubHeader>

			<Page container='fluid'>
				<Card tag='form' noValidate onSubmit={formik.handleSubmit}>
					<CardHeader>
						<CardLabel>
							<CardTitle>{'Company Profile'}</CardTitle>
						</CardLabel>
					</CardHeader>

					<CardBody>
						{/* Profile Info */}
						<Accordion
							id='accSample-shadow-1'
							shadow='default'
							className='mb-5'
							activeItemId='accor1-shadow-1'>
							<AccordionItem id='accor1-shadow-1' title='Profile Info' icon='Person'>
								<div className='row g-4'>
									<FormGroup
										id='validationName'
										label='Name'
										className='col-md-6'>
										<Input {...formik.getFieldProps('validationName')} />
										{formik.errors.validationName ? (
											<div className='text-danger'>
												{formik.errors.validationName}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
									<FormGroup
										id='validationContactPerson'
										label='Conatct Person'
										className='col-md-6'>
										<Input
											{...formik.getFieldProps('validationContactPerson')}
										/>
										{formik.errors.validationContactPerson ? (
											<div className='text-danger'>
												{formik.errors.validationContactPerson}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
								</div>
							</AccordionItem>
						</Accordion>

						{/* Contact Details */}
						<Accordion id='accSample-shadow-1' shadow='default' className='mb-5'>
							<AccordionItem
								id='accor1-shadow-2'
								title='Contact Details'
								icon='ContactPage'>
								<div className='row g-4'>
									<FormGroup
										id='validationEmail'
										label='Email'
										className='col-md-4'>
										<Input
											placeholder='Enter Email'
											{...formik.getFieldProps('validationEmail')}
										/>
										{formik.errors.validationEmail ? (
											<div className='text-danger'>
												{formik.errors.validationEmail}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
									<FormGroup
										id='validationMobile'
										label='Mobile No.'
										className='col-md-4'>
										<Input
											placeholder='Enter Mobile No.'
											{...formik.getFieldProps('validationMobile')}
											onChange={(e) =>
												handleContactChange(
													e as React.ChangeEvent<HTMLInputElement>,
													formik.setFieldValue,
												)
											}
											value={formik.values.validationMobile}
										/>
										<>
											{formik.errors.validationMobile && (
												<div className='text-danger'>
													{formik.errors.validationMobile}
												</div>
											)}
										</>
									</FormGroup>
									<FormGroup
										id='validationPhone'
										label='Phone No.'
										className='col-md-4'>
										<Input
											placeholder='Enter Phone No.'
											{...formik.getFieldProps('validationPhone')}
											onChange={(e) =>
												handleContactChange(
													e as React.ChangeEvent<HTMLInputElement>,
													formik.setFieldValue,
												)
											}
											value={formik.values.validationPhone}
										/>
										<>
											{formik.errors.validationPhone && (
												<div className='text-danger'>
													{formik.errors.validationPhone}
												</div>
											)}
										</>
									</FormGroup>
									<FormGroup id='validationFAX' label='FAX' className='col-md-4'>
										<Input
											placeholder='Enter FAX No.'
											{...formik.getFieldProps('validationFAX')}
										/>
										{formik.errors.validationFAX ? (
											<div className='text-danger'>
												{formik.errors.validationFAX}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
									<FormGroup
										id='validationWebsite'
										label='Website'
										className='col-md-4'>
										<Input
											placeholder='Enter Website'
											{...formik.getFieldProps('validationWebsite')}
										/>
										{formik.errors.validationWebsite ? (
											<div className='text-danger'>
												{formik.errors.validationWebsite}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
								</div>
							</AccordionItem>
						</Accordion>

						{/* Address Details */}
						<Accordion id='accSample-shadow-1' shadow='default' className='mb-5'>
							<AccordionItem
								id='accor1-shadow-2'
								title='Address Details'
								icon='House'>
								<div className='row g-4'>
									<FormGroup
										id='validationAddress'
										label='Address'
										className='col-md-4'>
										<Input {...formik.getFieldProps('validationAddress')} />
										{formik.errors.validationAddress ? (
											<div className='text-danger'>
												{formik.errors.validationAddress}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
									<FormGroup
										id='validationZip'
										label='Pincode'
										className='col-md-4'>
										<Input {...formik.getFieldProps('validationZip')} />
										{formik.errors.validationZip ? (
											<div className='text-danger'>
												{formik.errors.validationZip}
											</div>
										) : (
											<></>
										)}
									</FormGroup>

									<FormGroup
										id='validationCity'
										label='City'
										className='col-md-4'>
										<ReactSelect
											placeholder='Select City'
											classNamePrefix='companyDropdown'
											name='validationCity'
											isClearable
											options={dataCity}
											value={
												dataCity.find(
													(option) =>
														option.value ===
														formik.values.validationCity,
												) || null
											}
											onMenuOpen={() => {
												fetchCity({
													pageNo: 1,
													searchString: '',
													isNewSearch: true,
													selectedValue: formik.values.validationCity,
												});
											}}
											inputValue={citySearch}
											onInputChange={(inputValue, { action }) => {
												if (action === 'input-change') {
													setCitySearch(inputValue);
													setCityPage(1);
													setCityHasMore(true);
												}
											}}
											onMenuScrollToBottom={() => {
												if (cityHasMore) {
													fetchCity({
														pageNo: cityPage,
														searchString: citySearch,
													});
												}
											}}
											onChange={(selectedOption: any) => {
												formik.setFieldValue(
													'validationCity',
													selectedOption ? selectedOption?.value : '',
												);
												setCitySearch('');
											}}
											onBlur={formik.handleBlur}
											className={
												formik.touched.validationCity &&
												formik.errors.validationCity
													? 'is-invalid'
													: ''
											}
											isLoading={cityIsLoading}
											// If require
											menuPortalTarget={document.body}
											styles={{
												menuPortal: (base) => ({
													...base,
													zIndex: 9999,
												}),
											}}
										/>
										{formik.errors.validationCity ? (
											<div className='text-danger'>
												{formik.errors.validationCity}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
									<FormGroup
										id='validationState'
										label='State'
										className='col-md-4'>
										<ReactSelect
											placeholder='Select State'
											classNamePrefix='companyDropdown'
											name='validationState'
											isClearable
											options={dataState}
											value={
												dataState.find(
													(option) =>
														option.value ===
														formik.values.validationState,
												) || null
											}
											onMenuOpen={() => {
												fetchState({
													pageNo: 1,
													searchString: '',
													isNewSearch: true,
													selectedValue: formik.values.validationState,
												});
											}}
											inputValue={stateSearch}
											onInputChange={(inputValue, { action }) => {
												if (action === 'input-change') {
													setStateSearch(inputValue);
													setStatePage(1);
													setStateHasMore(true);
												}
											}}
											onMenuScrollToBottom={() => {
												if (stateHasMore) {
													fetchState({
														pageNo: statePage,
														searchString: stateSearch,
													});
												}
											}}
											onChange={(selectedOption: any) => {
												formik.setFieldValue(
													'validationState',
													selectedOption ? selectedOption?.value : '',
												);
												setStateSearch('');
											}}
											onBlur={formik.handleBlur}
											className={
												formik.touched.validationState &&
												formik.errors.validationState
													? 'is-invalid'
													: ''
											}
											isLoading={stateIsLoading}
											// If require
											menuPortalTarget={document.body}
											styles={{
												menuPortal: (base) => ({
													...base,
													zIndex: 9999,
												}),
											}}
										/>
										{formik.errors.validationState ? (
											<div className='text-danger'>
												{formik.errors.validationState}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
								</div>
							</AccordionItem>
						</Accordion>

						{/* Account Details */}
						<Accordion id='accSample-shadow-1' shadow='default' className='mb-5'>
							<AccordionItem id='accor1-shadow-3' title='Account Details' icon='Info'>
								<div className='row g-4'>
									<FormGroup id='validationPan' label='PAN' className='col-md-4'>
										<Input {...formik.getFieldProps('validationPan')} />
										{formik.errors.validationPan ? (
											<div className='text-danger'>
												{formik.errors.validationPan}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
									<FormGroup id='validationGst' label='GST' className='col-md-4'>
										<Input {...formik.getFieldProps('validationGst')} />
										{formik.errors.validationGst ? (
											<div className='text-danger'>
												{formik.errors.validationGst}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
									<FormGroup
										id='validationPlaceOfSupply'
										label='Place of Supply'
										className='col-md-4'>
										<ReactSelect
											placeholder='Select Place Of Supply'
											classNamePrefix='companyDropdown'
											name='validationPlaceOfSupply'
											isClearable
											options={dataPlaceOfSupply}
											value={
												dataPlaceOfSupply.find(
													(option) =>
														option.value ===
														formik.values.validationPlaceOfSupply,
												) || null
											}
											onMenuOpen={() => {
												fetchPlaceOfSupply({
													pageNo: 1,
													searchString: '',
													isNewSearch: true,
												});
											}}
											inputValue={placeOfSupplySearch}
											onInputChange={(inputValue, { action }) => {
												if (action === 'input-change') {
													setPlaceOfSupplySearch(inputValue);
													setPlaceOfSupplyPage(1);
													setPlaceOfSupplyHasMore(true);
												}
											}}
											onMenuScrollToBottom={() => {
												if (placeOfSupplyHasMore) {
													fetchPlaceOfSupply({
														pageNo: placeOfSupplyPage,
														searchString: placeOfSupplySearch,
													});
												}
											}}
											onChange={(selectedOption: any) => {
												formik.setFieldValue(
													'validationPlaceOfSupply',
													selectedOption ? selectedOption?.value : '',
												);
												setPlaceOfSupplySearch('');
											}}
											onBlur={formik.handleBlur}
											className={
												formik.touched.validationPlaceOfSupply &&
												formik.errors.validationPlaceOfSupply
													? 'is-invalid'
													: ''
											}
											isLoading={placeOfSupplyIsLoading}
											// If require
											menuPortalTarget={document.body}
											styles={{
												menuPortal: (base) => ({
													...base,
													zIndex: 9999,
												}),
											}}
										/>
										{formik.errors.validationPlaceOfSupply ? (
											<div className='text-danger'>
												{formik.errors.validationPlaceOfSupply}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
								</div>
							</AccordionItem>
						</Accordion>

						{/* Image */}
						<Accordion id='accSample-shadow-1' shadow='default' className='mb-5'>
							<AccordionItem id='accor1-shadow-4' title='Image' icon='Image'>
								<div className='row g-4'>
									<FormGroup
										id='validationOtherDetails'
										label='Image'
										className='col-md-4'>
										<Input
											type='file'
											name='accountImage'
											id='accountImage'
											accept='image/*'
											multiple
											onChange={handleUploadDoc}
										/>
									</FormGroup>
									{image.length === 0 ? (
										<div>
											<img
												src={filepath ? filepath : Empty}
												alt='accountImage'
												style={{ width: '8rem', height: '7rem' }}
											/>
										</div>
									) : (
										<div
											style={{
												display: 'flex',
												flexWrap: 'wrap',
												gap: '30px',
											}}>
											{image.map((img, index) => (
												<div key={index} style={{ position: 'relative' }}>
													<ImageViewer
														path={img?.doc}
														style={{
															width: '8rem',
															height: '5rem',
															cursor: 'pointer',
															objectFit: 'cover',
														}}
													/>
													{/* Delete Icon */}
													<Icon
														icon='Delete'
														style={{
															position: 'absolute',
															top: '5px',
															cursor: 'pointer',
															color: 'red',
															fontSize: '1.5rem',
														}}
														onClick={() =>
															handleDeleteImage(index, img?._id)
														}
													/>
												</div>
											))}
										</div>
									)}
								</div>
							</AccordionItem>
						</Accordion>
					</CardBody>

					<CardFooter style={{ flexDirection: 'row' }}>
						<CardFooterLeft>
							<Button type='submit' color='primary' className='fs-5'>
								{'Update'}
							</Button>
						</CardFooterLeft>
						<CardFooterRight>
							<Button
								type='reset'
								color='info'
								isOutline
								onClick={formik.handleReset}
								className='fs-5'>
								Reset
							</Button>
						</CardFooterRight>
					</CardFooter>
				</Card>
			</Page>
		</PageWrapper>
	);
};

export default UpdateCompanyProfile;
